import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IProcessStatus } from '@app/interfaces/process.interface';
import { ApiFacadeService } from '@app/services/api-facade.service';
import { CacheService } from '@app/services/cache.service';
import { WrapToasterService } from '@app/services/wrap-toaster-service';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { AddOrgParams, IOrg } from '../domain/models/admin';

@Injectable({ providedIn: 'root' })
export class AdminOrganizationsService {
	constructor(
		private apiFacade: ApiFacadeService,
		private toasterService: WrapToasterService,
		private cache: CacheService,
	) {}

	public addOrganization(params: AddOrgParams): Observable<{ success: boolean }> {
		const url = 'admin/organizations';

		return this.apiFacade.post<{ success: boolean }>(url, params).pipe(
			map(() => ({ success: true })),
			catchError((error) => {
				this.toasterService.handleRequestFailed(error);
				return of({ success: false });
			}),
		);
	}

	public deleteOrganization(orgId: number): Observable<{ success: IProcessStatus }> {
		const cacheKey = 'admin-organizations';
		const url = `admin/organizations/${orgId}`;

		return this.apiFacade.delete<{ Success: IProcessStatus }>(url).pipe(
			tap(() => {
				if (!this.cache.get(cacheKey)) {
					return;
				}

				const orgs = this.cache.get<IOrg[]>(cacheKey).filter((o) => o.org_id !== orgId);

				this.cache.set(cacheKey, orgs);
			}),
			map((res) => ({ success: IProcessStatus.done })),
			catchError((error) => {
				this.toasterService.handleRequestFailed(error);
				return of({ success: IProcessStatus.failed });
			}),
		);
	}

	public recollectDashboard(services: string[], orgId: number): Observable<any> {
		const payload = {
			aws_services: services,
			org_id: orgId,
		};
		const url = 'internal/customer/prepare_tasks';

		return this.apiFacade.post(url, payload).pipe(
			map((res) => {
				this.toasterService.handleSuccessMessage(res['message']);
				return res;
			}),
			catchError((error) => {
				this.toasterService.handleRequestFailed(error);
				return of(error);
			}),
		);
	}

	public renameOrganization(orgId: string, orgName: string): Observable<any> {
		const url = `admin/organizations/${orgId}/rename`;

		const params = new HttpParams().set('org_name', orgName);

		return this.apiFacade.put(url, {}, { params });
	}
}
